import styled from '@emotion/styled';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { config } from 'src/config/config';
import { TopicSelectionGroupSelectionType } from 'src/generated/webapp_gql';
import { Button, pxToRem } from '@modernhealth/vital-components';

import { topicSelectionPaths, useOnboardingGuard } from 'src/shared/hooks/useOnboardingGuard';
import { color, font } from 'src/shared/styles/variables';
import personWithKey from 'src/shared/svg/marginalia-information-security.svg';

import { BrandWrapper } from '../components/BrandWrapper';

interface StyledIconProps {
    iconUrl: string;
}

const StyledIcon = styled.i<StyledIconProps>(({ iconUrl }) => ({
    display: 'inline-block',
    backgroundImage: `url('${iconUrl}')`,
    width: '25rem',
    height: '25rem',
    backgroundSize: 'cover',
    backgroundPositionX: 'center',
}));

const Wrapper = styled.div({
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    minHeight: 'inherit',
    textAlign: 'center',
    minWidth: 350,
});

const LeftWrapper = styled.div({
    justifyContent: 'center',
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 'inherit',
    textAlign: 'center',
    minWidth: 350,
    marginRight: 150,
    width: '30%',
});

const HeadingText = styled.h2(
    {
        marginBottom: 30,
        textAlign: 'left',
    },
    font.heavy(font.size.extraLarge),
);

const Description = styled.p(
    {
        textAlign: 'left',
        color: color.ebonyClay,
        fontStyle: 'normal',
        letterSpacing: 0,
        marginBottom: 20,
    },
    font.roman(font.size.mediumExtended),
);

const BoldedText = styled.span(
    {
        textAlign: 'left',
        color: color.pickledBluewood,
        fontStyle: 'normal',
        letterSpacing: 0,
    },
    font.heavy(font.size.mediumExtended),
);

const BlueText = styled.a(
    {
        color: color.royalBlue,
    },
    font.heavy(font.size.mediumExtended),
);

type Props = RouteComponentProps;

export const PrivacyPage: React.FC<Props> = ({ history }) => {
    const shouldRender = useOnboardingGuard();

    const { t } = useTranslation();

    return shouldRender ? (
        <BrandWrapper>
            <Wrapper>
                <LeftWrapper>
                    <HeadingText>{t('onboarding:privacy_page.title')}</HeadingText>
                    <Description>
                        <Trans
                            t={t}
                            ns="onboarding"
                            i18nKey="privacy_page.first_description"
                            components={{
                                a: <BlueText href="mailto:help@joinmodernhealth.com" />,
                            }}
                        />
                    </Description>
                    <br />
                    <Description>
                        <Trans
                            t={t}
                            ns="onboarding"
                            i18nKey="privacy_page.second_description"
                            components={{
                                bold: <BoldedText />,
                                a: (
                                    <BlueText
                                        href={`${config.modernHealthURL}/privacy-policy`}
                                        target="_blank"
                                    />
                                ),
                            }}
                        />
                    </Description>
                    <Button
                        onClick={() =>
                            history.push(
                                `/onboarding-categories/${
                                    topicSelectionPaths[
                                        TopicSelectionGroupSelectionType.OnboardingFlow
                                    ]
                                }`,
                            )
                        }
                        variant="primary"
                        css={{
                            marginTop: pxToRem(20),
                            width: 'fit-content',
                            minWidth: 241,
                            paddingLeft: 0,
                            paddingRight: 0,
                        }}
                    >
                        {t('common.continue')}
                    </Button>
                </LeftWrapper>
                <StyledIcon iconUrl={personWithKey} />
            </Wrapper>
        </BrandWrapper>
    ) : null;
};
